import { Project } from '../../types';
import { ProjectStore } from '../storage/projects';
import { parseGraphML } from './graphml';
import { DEFAULT_CONFIG } from '../../types';

interface ExampleProjectLoadResult {
  success: boolean;
  project?: Project;
  error?: string;
}

interface ExampleMetadata {
  importedAt: number;
  contentHash: string;
}

interface ExampleIndex {
  examples: Array<{
    filename: string;
    contentHash: string;
    lastModified: number;
  }>;
}

export class ExampleProjectLoader {
  private static readonly EXAMPLES_DIR = '/examples';
  private static readonly EXAMPLE_INDEX = '/examples/index.json';

  private static async calculateSHA256(text: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  private static async getExampleIndex(): Promise<ExampleIndex> {
    const response = await fetch(this.EXAMPLE_INDEX);
    if (!response.ok) {
      throw new Error('Failed to fetch example index');
    }
    return response.json();
  }

  static async loadAllExamples(store: ProjectStore): Promise<void> {
    const startTime = performance.now();
    
    const metrics = {
      existingExamples: 0,
      availableFiles: 0,
      newlyLoaded: 0,
      updated: 0,
      unchanged: 0,
      orphansRemoved: 0,
      errors: 0
    };

    try {
      // Get all existing example projects
      const existingProjects = await store.getAllProjects(false);
      const exampleProjects = existingProjects.filter(p => p.type === 'example');
      metrics.existingExamples = exampleProjects.length;
      
      // Create a map for quick lookups: sourceFile -> project
      const existingExampleMap = new Map(
        exampleProjects
          .filter(p => p.sourceFile)
          .map(p => [p.sourceFile!, p])
      );

      // Get the example index
      const index = await this.getExampleIndex();
      metrics.availableFiles = index.examples.length;

      // Process each example from the index
      for (const example of index.examples) {
        try {
          const existingProject = existingExampleMap.get(example.filename);
          
          if (existingProject) {
            // Check if content has changed
            if (!existingProject.exampleMetadata || 
                existingProject.exampleMetadata.contentHash !== example.contentHash) {
              await this.resetExampleProject(existingProject.id, store);
              metrics.updated++;
            } else {
              metrics.unchanged++;
            }
          } else {
            await this.loadExampleProject(example.filename, store);
            metrics.newlyLoaded++;
          }
        } catch (error) {
          console.error('Failed to process example file:', { file: example.filename, error });
          metrics.errors++;
        }
      }

      // Clean up orphaned example projects
      const currentFiles = new Set(index.examples.map(e => e.filename));
      for (const project of exampleProjects) {
        if (project.sourceFile && !currentFiles.has(project.sourceFile)) {
          await store.softDeleteProject(project.id, true);
          metrics.orphansRemoved++;
        }
      }

    } catch (error) {
      console.error('Failed to load example projects:', error);
    } finally {
      const duration = (performance.now() - startTime).toFixed(0);
      console.log(`Example projects processed in ${duration}ms using index.json:`, {
        existing: metrics.existingExamples,
        available: metrics.availableFiles,
        new: metrics.newlyLoaded,
        updated: metrics.updated,
        unchanged: metrics.unchanged,
        orphansRemoved: metrics.orphansRemoved,
        errors: metrics.errors,
        indexUsed: true
      });
    }
  }

  private static async loadExampleProject(
    filename: string,
    store: ProjectStore
  ): Promise<ExampleProjectLoadResult> {
    try {
      const response = await fetch(`${this.EXAMPLES_DIR}/${filename}`);
      const text = await response.text();
      
      // Calculate content hash from the raw file content
      const contentHash = await this.calculateSHA256(text);
      const importedAt = Date.now();
      
      const { network, config, projectName, identifier } = await parseGraphML(
        new File([text], filename, { type: 'application/xml' })
      );

      // Create project with identifier from GraphML if available
      const project: Project = {
        id: crypto.randomUUID(),
        identifier: identifier || '', // Use identifier from GraphML if available
        name: projectName || filename.replace(/^\d{2}/, '').replace(/\.graphml$/, ''),
        lastEdited: Date.now(),
        type: 'example',
        sourceFile: filename,
        config: config || DEFAULT_CONFIG,
        network,
        exampleMetadata: {
          importedAt,
          contentHash
        }
      };

      // Only ensure identifier if one wasn't provided in the GraphML file
      const projectWithIdentifier = identifier 
        ? project 
        : await store.ensureProjectIdentifier(project);

      await store.saveProject(projectWithIdentifier);
      return { success: true, project: projectWithIdentifier };
    } catch (error) {
      return { 
        success: false, 
        error: `Failed to load example project ${filename}: ${error}` 
      };
    }
  }

  static async resetExampleProject(
    projectId: string,
    store: ProjectStore
  ): Promise<boolean> {
    const project = await store.getProject(projectId);
    if (!project || project.type !== 'example' || !project.sourceFile) {
      return false;
    }

    // Soft delete the existing project first
    await store.softDeleteProject(projectId, true);
    
    // Load fresh copy
    const result = await this.loadExampleProject(project.sourceFile, store);
    return result.success;
  }
} 